import { AnalyticsFilters, HtmlStepperForm } from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DashboardContext } from '../../contexts/DashboardContext';
import { UserContext } from '../../contexts/UserContext';
import { functions } from '../../Firebase';
import { quizFormsQuery } from '../../services/QuizService';
import { analyticsRangeToDates } from '../../utils';
import { extractTextFromQuillTrimmed } from '../../utils/quill';

interface StepAnalytics {
  stepId: string;
  stepIndex: number;
  stepName?: string;
  views: number;
  completions: number;
  conversionRate: number;
  averageTimeMs: number;
  dropOffRate: number;
}

interface FormAnalytics {
  formId: string;
  quizId?: string;
  totalViews: number;
  totalCompletions: number;
  completionRate: number;
  averageCompletionTimeMs: number;
  stepAnalytics: StepAnalytics[];
}

interface StepperFormAnalyticsResult {
  totalViews: number;
  totalCompletions: number;
  completionRate: number;
  averageCompletionTimeMs: number;
  formAnalytics: FormAnalytics[];
}

/** Step name lookup: formId -> stepId -> stepName */
type StepNameLookup = Map<string, Map<string, string>>;

const formatTime = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

/**
 * Build a step name lookup from an HtmlStepperForm.
 * Uses the inline production data if available (legacy), otherwise development.
 * Note: After Cloud Storage migration, inline production may be absent —
 * falling back to development is acceptable since step names match.
 */
const buildStepNameMapFromForm = (
  form: HtmlStepperForm,
): Map<string, string> => {
  const formData = form.production ?? form.development;
  const stepNameMap = new Map<string, string>();
  formData?.steps?.forEach((step) => {
    if (step.name) {
      stepNameMap.set(step.id, step.name);
    }
  });
  return stepNameMap;
};

export interface StepperAnalyticsProps {
  /** Campaign/DynamicPage ID - required for fetching analytics */
  dynamicPageId: string;
  /** Forms for name lookup - array of { id, name } */
  forms?: Array<{ id: string; name: string }>;
  /** Analytics filters (advanced filter rules) */
  analyticsFilters?: AnalyticsFilters;
  /** Custom title for the component header */
  title?: string;
  /** Hide the header (title + form selector) */
  hideHeader?: boolean;
}

export default function StepperAnalytics({
  dynamicPageId,
  forms = [],
  analyticsFilters,
  title = 'Stepper Form Analytics',
  hideHeader = false,
}: StepperAnalyticsProps) {
  const { analyticsRange, quizzes } = useContext(DashboardContext);
  const { selectedAccount } = useContext(UserContext);

  const [analytics, setAnalytics] = useState<StepperFormAnalyticsResult | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string>('all');
  // Step name lookup fetched from quiz forms for backward compatibility
  const [quizStepNameLookup, setQuizStepNameLookup] = useState<StepNameLookup>(
    new Map(),
  );

  // Build quizzes lookup map: quizId -> quiz name
  const quizzesMap = useMemo(() => {
    return new Map(
      quizzes?.map((q) => [q.id, extractTextFromQuillTrimmed(q.data().name)]) ??
        [],
    );
  }, [quizzes]);

  // Build forms lookup map for efficient name resolution (includes both regular forms and quiz names)
  const formsMap = useMemo(() => {
    const map = new Map(
      forms.map((f) => [f.id, extractTextFromQuillTrimmed(f.name)]),
    );
    return map;
  }, [forms]);

  /**
   * Resolve the display name for a form entry in analytics.
   * Priority: forms prop -> quiz name (via quizId) -> formId fallback
   */
  const resolveFormName = useCallback(
    (formAnalytics: FormAnalytics): string => {
      // 1. Check regular forms map
      const formName = formsMap.get(formAnalytics.formId);
      if (formName) return formName;

      // 2. Check quiz name via quizId
      if (formAnalytics.quizId) {
        const quizName = quizzesMap.get(formAnalytics.quizId);
        if (quizName) return quizName;
      }

      // 3. Fallback to formId
      return formAnalytics.formId;
    },
    [formsMap, quizzesMap],
  );

  /**
   * Resolve the display name for a step.
   * Priority: stepName from analytics -> quiz form step name lookup -> stepId fallback
   */
  const resolveStepName = useCallback(
    (formId: string, step: StepAnalytics): string => {
      // 1. Use stepName from analytics (new data has it)
      if (step.stepName) return extractTextFromQuillTrimmed(step.stepName);

      // 2. Fallback: look up from fetched quiz form steps
      const formSteps = quizStepNameLookup.get(formId);
      if (formSteps) {
        const name = formSteps.get(step.stepId);
        if (name) return extractTextFromQuillTrimmed(name);
      }

      // 3. Fallback to stepId
      return step.stepId;
    },
    [quizStepNameLookup],
  );

  // Get all unique form IDs from all websites
  const availableForms = useMemo(() => {
    return analytics?.formAnalytics?.map((form) => ({
      formId: form.formId,
      formName: resolveFormName(form),
    }));
  }, [analytics, resolveFormName]);

  const formOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All Forms' },
      ...(availableForms?.map((form) => ({
        value: form.formId,
        label: form.formName || form.formId,
      })) || []),
    ];
  }, [availableForms]);

  const fetchAnalytics = useCallback(async () => {
    if (!dynamicPageId || !selectedAccount?.id || !analyticsRange) return;

    setLoading(true);
    setError(null);

    try {
      const getStepperFormAnalytics = httpsCallable<
        any,
        StepperFormAnalyticsResult
      >(functions, 'analytics/stepperFormAnalytics');

      const filters: any = {
        campaignId: dynamicPageId,
      };

      const dateRange = analyticsRangeToDates(analyticsRange);

      // Transform filter rules to the format expected by the backend
      const advancedFilterRules = analyticsFilters?.rules ?? [];

      const result = await getStepperFormAnalytics({
        accountId: selectedAccount.id,
        filters,
        advancedFilterRules:
          advancedFilterRules.length > 0 ? advancedFilterRules : undefined,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      setAnalytics(result.data);

      // For backward compatibility: fetch quiz forms to resolve step names
      // when stepName is not present in the analytics data
      const needsStepNameLookup = result.data.formAnalytics.some(
        (form) =>
          form.quizId && form.stepAnalytics.some((step) => !step.stepName),
      );

      if (needsStepNameLookup) {
        const quizIds = [
          ...new Set(
            result.data.formAnalytics
              .filter((f) => f.quizId)
              .map((f) => f.quizId!),
          ),
        ];

        const lookup: StepNameLookup = new Map();

        await Promise.all(
          quizIds.map(async (quizId) => {
            try {
              const formsSnapshot = await getDocs(
                quizFormsQuery(selectedAccount.id, quizId),
              );
              formsSnapshot.docs.forEach((formDoc) => {
                const form = formDoc.data() as HtmlStepperForm;
                const stepMap = buildStepNameMapFromForm(form);
                if (stepMap.size > 0) {
                  lookup.set(formDoc.id, stepMap);
                }
              });
            } catch {
              // Silently skip if quiz forms can't be fetched
            }
          }),
        );

        setQuizStepNameLookup(lookup);
      }
    } catch (err) {
      console.error('Failed to fetch stepper form analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dynamicPageId, selectedAccount, analyticsRange, analyticsFilters]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const filteredAnalytics =
    selectedFormId === 'all'
      ? analytics?.formAnalytics || []
      : analytics?.formAnalytics.filter(
          (form) => form.formId === selectedFormId,
        ) || [];

  return (
    <div className="mt-3 space-y-3">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>

          <div className="flex gap-4">
            {!!analytics?.formAnalytics.length && !!availableForms?.length && (
              <div className="w-64">
                <Select
                  value={selectedFormId}
                  onValueChange={(value: string) => setSelectedFormId(value)}
                  disabled={loading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content Area */}
      {loading && (
        <div className="flex h-64 items-center justify-center">
          <Loader />
          <span className="ml-2 text-gray-600">Loading analytics...</span>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {!loading &&
        !error &&
        (!analytics || analytics.formAnalytics.length === 0) && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-16 text-center">
            <div className="mx-auto max-w-md">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No stepper form data available
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Stepper forms need to have analytics events to display data
                here.
              </p>
              <div className="mt-6">
                <div className="rounded-md bg-blue-50 p-4">
                  <div className="text-sm text-blue-700">
                    <strong>How to get analytics data:</strong>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-left">
                      <li>Create stepper forms in your campaigns</li>
                      <li>Ensure forms are published and receiving traffic</li>
                      <li>
                        Analytics will appear once users interact with your
                        forms
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {!loading &&
        !error &&
        analytics &&
        analytics.formAnalytics.length > 0 && (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {analytics.totalViews.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Views</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-green-600">
                  {analytics.totalCompletions.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Completions</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-blue-600">
                  {formatPercentage(analytics.completionRate)}
                </div>
                <div className="text-sm text-gray-600">Completion Rate</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-2xl font-bold text-purple-600">
                  {formatTime(analytics.averageCompletionTimeMs)}
                </div>
                <div className="text-sm text-gray-600">
                  Avg. Completion Time
                </div>
              </div>
            </div>

            {/* Forms Analytics */}
            {filteredAnalytics.map((formAnalytics) => (
              <div
                key={formAnalytics.formId}
                className="rounded-lg border border-gray-200 bg-white"
              >
                <div className="border-b border-gray-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {resolveFormName(formAnalytics)}
                  </h3>
                  <div className="text-sm text-gray-500">
                    {formAnalytics.formId}
                    {formAnalytics.quizId && (
                      <span className="ml-2 text-xs text-gray-400">
                        (Quiz:{' '}
                        {quizzesMap.get(formAnalytics.quizId) ||
                          formAnalytics.quizId}
                        )
                      </span>
                    )}
                  </div>

                  {(availableForms?.length ?? 0) > 1 && (
                    <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                      <div>
                        <div className="text-xl font-bold text-gray-900">
                          {formAnalytics.totalViews.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Views</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-green-600">
                          {formAnalytics.totalCompletions.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Completions</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-blue-600">
                          {formatPercentage(formAnalytics.completionRate)}
                        </div>
                        <div className="text-xs text-gray-600">
                          Completion Rate
                        </div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-purple-600">
                          {formatTime(formAnalytics.averageCompletionTimeMs)}
                        </div>
                        <div className="text-xs text-gray-600">Avg. Time</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step Analytics Table */}
                <div className="py-6">
                  <h4 className="text-md mb-4 px-6 font-medium text-gray-900">
                    Step-by-Step Analysis
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 px-6">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Step
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Views
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Completions
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Conversion Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Avg. Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                            Drop-off Rate
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {formAnalytics.stepAnalytics.map((step) => (
                          <tr key={step.stepId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {resolveStepName(formAnalytics.formId, step)}
                              </div>
                              <div className="text-sm text-gray-500">
                                Step {step.stepIndex + 1}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                              {step.views.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-green-600">
                              {step.completions.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-blue-600">
                              {formatPercentage(step.conversionRate)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-purple-600">
                              {formatTime(step.averageTimeMs)}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-red-600">
                              {formatPercentage(step.dropOffRate)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
    </div>
  );
}
